import { Check, Info, Minus, Plus } from "lucide-react";
import { money } from "../lib/format";
import { mediaUrl } from "../lib/api";

/** Vendor photos arrive as OUR proxy path (/api/images/...), which must be
 *  resolved against the API origin; our local fallback artwork is a plain public
 *  asset and passes through untouched. */
const addonSrc = (addon) => (addon.imageUrl ? mediaUrl(addon.imageUrl) : FALLBACK);

const FALLBACK = "/addons/fallback.svg";

const FlexNote = () => (
  <p className="mt-2 text-xs leading-relaxed text-slate-600">
    Cancel up to <strong className="font-bold text-navy">24 hours</strong> before your trip for a full rental refund.
    Without it we need <strong className="font-bold text-navy">72 hours</strong> notice and a{" "}
    <strong className="font-bold text-navy">$75</strong> cancellation fee.{" "}
    <a href="/rental-requirements" className="font-bold text-lake hover:underline">
      Read the policy
    </a>
  </p>
);

const PetNote = () => (
  <p className="mt-2 text-xs leading-relaxed text-slate-600">
    Dogs up to 120 lbs, on select vessels. One flat fee covers your trip — mention your pet when you arrive.
  </p>
);

const GearCard = ({ addon, qty, onChange }) => {
  const soldOut = !addon.available;
  const single = addon.maxQuantity === 1;
  const on = qty > 0;

  return (
    <div
      data-testid={`addon-card-${addon.assetId}`}
      className={`overflow-hidden rounded-2xl border transition-all ${
        soldOut
          ? "border-sand-border opacity-55"
          : on
            ? "border-ember bg-ember/[0.05]"
            : "border-sand-border hover:border-lake/50"
      }`}
    >
      <div className="aspect-square bg-sand-card">
        <img
          src={addonSrc(addon)}
          data-testid={`addon-card-image-${addon.assetId}`}
          alt={addon.label}
          loading="lazy"
          className={`h-full w-full object-cover ${soldOut ? "grayscale" : ""}`}
          onError={(e) => {
            e.currentTarget.src = FALLBACK;
          }}
        />
      </div>
      <div className="p-4">
        <p className="text-sm font-bold text-navy">{addon.label}</p>
        <p className="mt-0.5 text-xs font-semibold text-slate-500">{money(addon.amount)} for your trip</p>

        {soldOut ? (
          <p data-testid={`addon-soldout-${addon.assetId}`} className="mt-3 text-xs font-bold text-ember">
            Not available on this date
          </p>
        ) : single ? (
          <button
            type="button"
            data-testid={`addon-toggle-${addon.assetId}`}
            aria-pressed={on}
            onClick={() => onChange(on ? 0 : 1)}
            className={`mt-3 inline-flex w-full items-center justify-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold transition-colors ${
              on ? "bg-ember text-white" : "border border-navy/15 bg-white text-navy hover:border-lake"
            }`}
          >
            {on ? (
              <>
                <Check className="h-3.5 w-3.5" aria-hidden="true" /> Added
              </>
            ) : (
              "Add"
            )}
          </button>
        ) : (
          <div className="mt-3 flex items-center justify-between gap-2">
            <button
              type="button"
              data-testid={`addon-stepper-${addon.assetId}-decrement`}
              aria-label={`Fewer ${addon.label}`}
              disabled={qty <= 0}
              onClick={() => onChange(Math.max(0, qty - 1))}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-sand-border text-navy disabled:opacity-30"
            >
              <Minus className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
            <span data-testid={`addon-stepper-${addon.assetId}-value`} aria-live="polite" className="text-sm font-bold text-navy">
              {qty}
            </span>
            <button
              type="button"
              data-testid={`addon-stepper-${addon.assetId}-increment`}
              aria-label={`More ${addon.label}`}
              disabled={qty >= (addon.maxQuantity || 9)}
              onClick={() => onChange(qty + 1)}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-sand-border text-navy disabled:opacity-30"
            >
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            </button>
          </div>
        )}
        {!soldOut && addon.maxQuantity > 1 && (
          <p className="mt-2 text-[11px] text-slate-500">{addon.maxQuantity} available</p>
        )}
      </div>
    </div>
  );
};

const OptionRow = ({ addon, qty, onChange }) => {
  const on = qty > 0;
  const isFlex = addon.label?.toLowerCase().includes("flexibility");
  const isPets = addon.label?.toLowerCase().startsWith("pet");

  return (
    <div
      data-testid={`addon-option-${addon.assetId}`}
      className={`rounded-2xl border p-4 transition-all ${
        on ? (isFlex ? "border-lake bg-lake/[0.07]" : "border-ember bg-ember/[0.05]") : "border-sand-border"
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <img
            src={addonSrc(addon)}
            alt=""
            aria-hidden="true"
            loading="lazy"
            data-testid={`addon-option-image-${addon.assetId}`}
            className="h-11 w-11 rounded-xl object-cover"
            onError={(e) => {
              e.currentTarget.src = FALLBACK;
            }}
          />
          <div>
            <p className="text-sm font-bold text-navy">
              {addon.label}
              {isFlex && (
                <span className="ml-2 rounded-full bg-lake/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-lake">
                  Recommended
                </span>
              )}
            </p>
            <p className="mt-0.5 text-xs font-semibold text-slate-500">{money(addon.amount)} for your trip</p>
          </div>
        </div>
        <button
          type="button"
          data-testid={`addon-toggle-${addon.assetId}`}
          aria-pressed={on}
          onClick={() => onChange(on ? 0 : 1)}
          className={`shrink-0 rounded-full px-4 py-2 text-xs font-bold transition-colors ${
            on ? "bg-ember text-white" : "border border-navy/15 bg-white text-navy hover:border-lake"
          }`}
        >
          {on ? "Added" : "Add"}
        </button>
      </div>
      {isFlex && <FlexNote />}
      {isPets && <PetNote />}
    </div>
  );
};

export const AddonPicker = ({ addons = [], quantities = {}, onChange }) => {
  const gear = addons.filter((a) => a.group === "gear");
  const options = addons.filter((a) => a.group !== "gear");
  if (!addons.length) return null;

  return (
    <div data-testid="addons-section" className="mt-8">
      {gear.length > 0 && (
        <fieldset>
          <legend className="eyebrow text-lake">Water toys &amp; gear</legend>
          <p className="mt-1 text-xs text-slate-500">
            One flat price for your whole trip — 3 hours or all day, same cost.
          </p>
          <div data-testid="addons-gear-grid" className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {gear.map((a) => (
              <GearCard key={a.assetId} addon={a} qty={quantities[a.assetId] || 0} onChange={(v) => onChange(a.assetId, v)} />
            ))}
          </div>
        </fieldset>
      )}

      {options.length > 0 && (
        <fieldset className="mt-7">
          <legend className="eyebrow text-lake">Options &amp; protection</legend>
          <div data-testid="addons-options-list" className="mt-3 space-y-3">
            {options.map((a) => (
              <OptionRow key={a.assetId} addon={a} qty={quantities[a.assetId] || 0} onChange={(v) => onChange(a.assetId, v)} />
            ))}
          </div>
        </fieldset>
      )}

      <p className="mt-4 flex items-start gap-2 text-xs text-slate-500">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        Gear is shared across the whole marina, so popular items can sell out for a date.
      </p>
    </div>
  );
};

export default AddonPicker;
